import React, { useEffect } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

const Toast = ({ message, type = 'success', onClose, duration = 3000 }) => {
  const [show, setShow] = React.useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(false);
      setTimeout(onClose, 300); // Wait for animation to finish
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const config = {
    success: {
      icon: CheckCircleIcon,
      bgColor: 'bg-success-50',
      borderColor: 'border-success-500',
      textColor: 'text-success-800',
      iconColor: 'text-success-500',
    },
    error: {
      icon: XCircleIcon,
      bgColor: 'bg-danger-50',
      borderColor: 'border-danger-500',
      textColor: 'text-danger-800',
      iconColor: 'text-danger-500',
    },
    warning: {
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-warning-50',
      borderColor: 'border-warning-500',
      textColor: 'text-warning-800',
      iconColor: 'text-warning-600',
    },
    info: {
      icon: InformationCircleIcon,
      bgColor: 'bg-info-50',
      borderColor: 'border-info-500',
      textColor: 'text-info-800',
      iconColor: 'text-info-500',
    },
  };

  const { icon: Icon, bgColor, borderColor, textColor, iconColor } = config[type];

  return (
    <Transition
      show={show}
      enter="transform transition duration-300 ease-out"
      enterFrom="translate-y-2 opacity-0 scale-95"
      enterTo="translate-y-0 opacity-100 scale-100"
      leave="transform transition duration-200 ease-in"
      leaveFrom="translate-y-0 opacity-100 scale-100"
      leaveTo="translate-y-2 opacity-0 scale-95"
    >
      <div
        className={`
          fixed top-20 right-4 z-[9999] max-w-sm w-full
          ${bgColor} ${textColor}
          border-l-4 ${borderColor}
          rounded-lg shadow-large
          p-4
          pointer-events-auto
        `}
      >
        <div className="flex items-start gap-3">
          <Icon className={`h-6 w-6 flex-shrink-0 ${iconColor}`} />
          <p className="flex-1 text-sm font-medium leading-relaxed">
            {message}
          </p>
          <button
            onClick={() => {
              setShow(false);
              setTimeout(onClose, 300);
            }}
            className={`
              flex-shrink-0 rounded-lg p-1
              ${textColor} hover:bg-black/5
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${type}-500
            `}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </Transition>
  );
};

export default Toast;
